import { useMemo } from 'react';
import { APIBinderData } from 'types/api.types';
import {
  processGaragingAddressData,
  processLeasedVehicleData,
  processPleasureUseData,
  processBusinessUseData,
  processAnnualDrivingDistanceData,
  processCarPoolingData,
  processTypeOfFuelData,
  processUnrepairedDamageData,
  processModifiedCustomizedData,
  processExceptionalUsageData,
  processMaritalStatusData,
  processDateFirstLicensedData,
  processGraduatedLicensingData,
  processUnlistedDriversData,
  processUnlistedVehiclesData,
  processClaimsData,
  processConvictionsData,
  processLapsesData,
  processQuoteDetailsData,
  processPolicyChangeFormsData,
  processRemarksData,
  processUploadSummaryData,
  processMVRLicenseStatusData,
} from '../processors';
import { processCancellationData } from '../processors/cancellationProcessor';
import { processGapInInsuranceImmediatelyPriorData } from '../processors/gapInInsuranceImmediatelyPriorProcessor';
import { processMultiLineDiscountData } from '../processors/multiLineDiscountProcessor';
import { processPrincipalDriverAssignmentData } from '../processors/principalDriverAssignmentProcessor';
import { processUnknownOperatorClaimsData } from '../processors/unknownOperatorClaimsProcessor';

export interface BinderSectionData {
  title: string;
  description: string;
  fieldName?: string;
  isValid: boolean;
  message?: string | null;
  data?: any; // Processed section-specific data
}

export interface UseBinderSectionDataReturn {
  [sectionId: string]: BinderSectionData;
}

export const useBinderSectionData = (
  binderReportData: APIBinderData,
): UseBinderSectionDataReturn => {
  return useMemo(() => {
    if (!binderReportData) return {};

    const applicationData = binderReportData.application_data;
    const validationData = binderReportData.validation_data;
    const remarksData = binderReportData.remarks_data;
    const driversSummary = applicationData?.drivers_summary;
    const validations = validationData?.validations || [];
    const vehicles = applicationData?.vehicles || [];
    const drivers = applicationData?.drivers || [];

    const validationMap = new Map();
    validations.forEach((v: any) => {
      if (v.validation_id) {
        validationMap.set(v.validation_id, v);
      }
    });

    const remarksMap = new Map();
    (remarksData || []).forEach((r: any) => {
      if (r.validation_key) {
        if (!remarksMap.has(r.validation_key)) {
          remarksMap.set(r.validation_key, []);
        }
        remarksMap.get(r.validation_key).push(r);
      }
    });

    return {
      address: processGaragingAddressData(
        binderReportData,
        [
          ...(remarksMap.get('008') || []),
          ...(remarksMap.get('008a') || []),
          ...(remarksMap.get('008b') || []),
        ],
        validationMap.get('008b') ||
          validationMap.get('008a') ||
          validationMap.get('008'),
      ),
      'leased-vehicle': processLeasedVehicleData(vehicles),
      'pleasure-use': processPleasureUseData(
        vehicles,
        remarksMap.get('012') || [],
        validationMap.get('012'),
      ),
      'business-use': processBusinessUseData(
        vehicles,
        remarksMap.get('013') || [],
        validationMap.get('013'),
      ),
      'annual-driving-distance': processAnnualDrivingDistanceData(
        vehicles,
        remarksMap.get('014') || [],
        validationMap.get('014'),
      ),
      'car-pooling': processCarPoolingData(
        vehicles,
        remarksMap.get('car_pooling') || [],
      ),
      'type-of-fuel': processTypeOfFuelData(
        vehicles,
        remarksMap.get('016') || [],
        validationMap.get('016'),
      ),
      'unrepaired-damage': processUnrepairedDamageData(
        vehicles,
        remarksMap.get('unrepaired_damage') || [],
      ),
      'modified-customized': processModifiedCustomizedData(
        vehicles,
        remarksMap.get('modified_customized') || [],
      ),
      'exceptional-usage': processExceptionalUsageData(
        applicationData,
        validationMap.get('020'),
        remarksMap.get('additional_risk') || [],
      ),
      'marital-status': processMaritalStatusData(
        applicationData,
        validationMap.get('022'),
      ),
      'date-first-licensed': processDateFirstLicensedData(
        binderReportData,
        [...(remarksMap.get('023') || []), ...(remarksMap.get('023a') || [])],
        validationMap.get('023a') || validationMap.get('023'),
      ),
      'graduated-licensing': processGraduatedLicensingData(
        binderReportData,
        remarksMap.get('024') || [],
        validationMap.get('024'),
      ),
      'principal-driver-assignment': processPrincipalDriverAssignmentData(
        binderReportData,
        validationMap.get('025'),
        remarksMap.get('025') || [],
      ),
      'unlisted-drivers': processUnlistedDriversData(
        applicationData,
        validationMap.get('026'),
      ),
      'unlisted-vehicles': processUnlistedVehiclesData(
        applicationData,
        validationMap.get('021'),
      ),
      'suspensions-6-years': processCancellationData(
        {
          title: 'Suspensions & Cancellations (6 years)',
          description:
            'Check for driver license suspensions and policy cancellations within the past 6 years.',
          isValid: !Boolean(
            driversSummary?.is_suspended_or_cancelled_last_6_years,
          ),
        },
        remarksMap.get('027') || [],
        validationMap.get('027'),
      ),
      'cancellations-3-years': processCancellationData(
        {
          title: 'Cancellations (3 years)',
          description:
            'Check for policy cancellations within the past 3 years.',
          isValid: !Boolean(driversSummary?.is_cancelled_last_3_years),
        },
        remarksMap.get('028') || [],
        validationMap.get('028'),
      ),
      'misrepresentation-3-years': processCancellationData(
        {
          title: 'Misrepresentation (3 years)',
          description:
            'Check for any misrepresentation issues within the past 3 years.',

          isValid: !Boolean(driversSummary?.is_misrepresentation_last_3_years),
        },
        remarksMap.get('029') || [],
        validationMap.get('029'),
      ),
      'gap-in-insurance-immediately-prior':
        processGapInInsuranceImmediatelyPriorData(
          drivers,
          [...(remarksMap.get('090') || []), ...(remarksMap.get('090a') || [])],
          validationMap.get('090a') || validationMap.get('090'),
        ),
      'multi-line-discount': processMultiLineDiscountData(
        remarksMap.get('098') || [],
        validationMap.get('098'),
      ),
      'mvr-license-status': processMVRLicenseStatusData(
        binderReportData,
        validationMap.get('101'),
      ),
      claims: processClaimsData(
        binderReportData,
        remarksMap.get('032') || [],
        validationMap.get('032'),
      ),
      'unknown-operator-claims': processUnknownOperatorClaimsData(
        binderReportData,
        validationMap.get('032'),
      ),
      convictions: processConvictionsData(
        binderReportData,
        validationMap.get('033'),
      ),
      lapses: processLapsesData(binderReportData, validationMap.get('034')),
      'quote-details': processQuoteDetailsData(binderReportData),
      'policy-change-forms': processPolicyChangeFormsData(applicationData),
      'upload-summary': {
        title: 'Upload Summary',
        description: '',
        isValid: true,
        data: processUploadSummaryData(binderReportData)?.data,
      },
      remarks: processRemarksData(remarksData || []),
    };
  }, [binderReportData]);
};
